import React, { useCallback, useEffect, useState } from 'react';

import { DateRangeQueryFilter, IconBroadcast } from '@trqk/components';
import {
    dateRangeToFilterInput,
    getDateRangeBefore,
    getWebpackEnvVar,
    locale,
    getAllListPages,
} from '@trqk/utils';

import { KPI, WorkDetailCard, WorkDetailSection } from '..';
import { DetectionsBySourceChart, WorkDetailDetectionsTable } from '.';
import { useModal, useTokenModal, useToast } from '../../../../../hooks';

const maxSize = 10;

const defaultDateRangeOption = {
    label: locale.enUS.DATE_RANGES.PAST_7_DAYS,
    value: getDateRangeBefore(new Date(), { days: 7 }),
};

interface DetectionsProps {
    trqkAudioId: string;
}

export const Detections = ({ trqkAudioId }: DetectionsProps): JSX.Element => {
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const modal = useModal();
    const { getToken } = useTokenModal();
    const { add: addToast } = useToast();
    const [selectedDateRangeLabel, setSelectedDateRangeLabel] = useState(
        defaultDateRangeOption.label,
    );
    const [filterInput, setFilterInput] =
        useState<TableBmatDetectionFilterInput>({
            timestamp: dateRangeToFilterInput(defaultDateRangeOption.value),
            audio_id: {
                eq: trqkAudioId,
            },
        });
    const [loading, setLoading] = useState(false);
    const [bmatDetections, setBmatDetections] = useState<BmatDetection[]>([]);

    const getAllDetections = useCallback(
        (filter: TableBmatDetectionFilterInput) => {
            setLoading(true);

            getAllListPages<BmatDetection, TableBmatDetectionFilterInput>({
                filter,
                getToken,
                query: `
                    query listBmatDetections(
                        $filter: TableBmatDetectionFilterInput
                        $nextToken: String
                        $limit: Int
                    ) {
                        listBmatDetections(
                            filter: $filter
                            nextToken: $nextToken
                            limit: $limit
                        ) {
                            items {
                                audio_id
                                channel
                                detection_id
                                duration
                                episode
                                episode_number
                                filename
                                show
                                song_id
                                timestamp
                            }
                            nextToken
                        }
                    }
                `,
                queryName: 'listBmatDetections',
                trqkDirectoryUrl,
            })
                .then((data: BmatDetection[]): void => {
                    setBmatDetections(data);
                    setLoading(false);
                })
                .catch((): void => {
                    setLoading(false);
                    addToast({
                        kind: 'warning-banner',
                        content:
                            locale.enUS.UTILS.ERRORS.GET_ALL_TUNESAT_DETECTIONS,
                    });
                });
        },
        [addToast, getToken],
    );

    useEffect(() => {
        getAllDetections(filterInput);
    }, [filterInput]);

    return (
        <>
            <WorkDetailSection>
                <div className="flex items-center my-6">
                    <h5 className="mr-3">Trqk Detections for:</h5>
                    <DateRangeQueryFilter<TableBmatDetectionFilterInput>
                        defaultOption={defaultDateRangeOption}
                        filterInput={filterInput}
                        fieldName="timestamp"
                        modal={modal}
                        setFilterInput={setFilterInput}
                        selectedLabel={selectedDateRangeLabel}
                        setSelectedLabel={setSelectedDateRangeLabel}
                    />
                </div>
                <div className="grid w-2/3 grid-cols-2 gap-4">
                    <KPI
                        bgClass="bg-primary-500"
                        Icon={IconBroadcast}
                        label="Network & Cable Detections"
                        value={bmatDetections.length.toString()}
                    />
                </div>
            </WorkDetailSection>
            <WorkDetailSection>
                <div className="grid grid-cols-2 gap-4">
                    <WorkDetailCard>
                        <div className="mb-8 text-lg font-semibold text-tertiary-500">
                            Detections by Source
                        </div>
                        <DetectionsBySourceChart
                            detections={bmatDetections}
                            loading={loading}
                            maxSize={maxSize}
                        />
                    </WorkDetailCard>
                    <WorkDetailCard>
                        <div className="mb-8 text-lg font-semibold text-tertiary-500">
                            Detection Data
                        </div>
                        <WorkDetailDetectionsTable
                            detections={bmatDetections}
                            loading={loading}
                        />
                    </WorkDetailCard>
                </div>
            </WorkDetailSection>
        </>
    );
};
